import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MasterCropData,
  MasterLabelDiffSummary,
  MasterLabelReviewIssueType,
  MasterUPCData,
  SectionTarget,
} from '../types';
import { ActionMode } from './useUPCActions';
import { computeMasterLabelDiffSummary } from '../services/masterLabelState';
import {
  resolveDesignTargetBarcode,
  resolveDraftDesignVerifiedBarcode,
} from '../utils/masterBarcodeRows';

const DESIGN_COLORS = ['#f87171', '#60a5fa', '#34d399', '#c084fc', '#fbbf24', '#f472b6', '#2dd4bf', '#fb923c'];
const MASTER_LABEL_MOVED_HIGHLIGHT_MS = 5000;

interface UndoEntry {
  label: string;
  upcData: MasterUPCData[];
}

interface SelectedCropDesignInfo {
  designId: string;
  cropId: string;
  isAlreadyRep: boolean;
}

export interface UseMasterLabelEditorOptions {
  notify: (text: string, type?: 'success' | 'warning' | 'error') => void;
}

type CropLocation =
  | { upcIdx: number; section: 'design'; designIdx: number; cropIdx: number }
  | { upcIdx: number; section: 'pending' | 'no-design' | 'carton' | 'inactive'; cropIdx: number };

function cloneUpcs(upcs: MasterUPCData[]): MasterUPCData[] {
  return structuredClone(upcs);
}

function makeDraftDesignId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `draft:${crypto.randomUUID()}`;
  }
  return `draft:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function sortCrops(crops: MasterCropData[]): MasterCropData[] {
  return [...crops].sort((a, b) => {
    const aScan = a.source === 'scan' ? 0 : 1;
    const bScan = b.source === 'scan' ? 0 : 1;
    if (aScan !== bScan) return aScan - bScan;
    return a.id.localeCompare(b.id);
  });
}

function sortDesignCrops(crops: MasterCropData[]): MasterCropData[] {
  return [...crops].sort((a, b) => {
    const aFront = a.orientation === 'not-front' ? 1 : 0;
    const bFront = b.orientation === 'not-front' ? 1 : 0;
    if (aFront !== bFront) return aFront - bFront;
    const aScan = a.source === 'scan' ? 0 : 1;
    const bScan = b.source === 'scan' ? 0 : 1;
    if (aScan !== bScan) return aScan - bScan;
    return a.id.localeCompare(b.id);
  });
}

function cleanupDesigns(upc: MasterUPCData): void {
  upc.designs = upc.designs
    .filter((design) => {
      if (design.crops.length > 0) return true;
      if (!design.persistedDesignId) return false;
      return design.barcodeStatus !== 'unconfirmed';
    });
}

function normalizeRepresentativeCrop(design: MasterUPCData['designs'][number]): void {
  if (design.crops.length === 0) {
    design.representativeCropId = null;
    return;
  }
  const representativeCrop =
    design.crops.find((crop) => crop.id === design.representativeCropId) ?? null;
  const frontCrops = design.crops.filter(
    (crop) => crop.classificationType !== 'carton' && crop.orientation !== 'not-front'
  );

  if (representativeCrop && representativeCrop.orientation !== 'not-front') {
    return;
  }
  if (frontCrops.length > 0) {
    design.representativeCropId = frontCrops[0].id;
    return;
  }
  if (representativeCrop) {
    return;
  }
  design.representativeCropId = design.crops[0].id;
}

function applyCropBarcode(crop: MasterCropData, normalizedBarcode?: string): void {
  if (!normalizedBarcode) return;
  crop.normalizedBarcode = normalizedBarcode;
}

function promoteCropToProductPreview(crop: MasterCropData): void {
  crop.classificationType = 'product';
  if (!crop.orientation) {
    crop.orientation = 'front';
  }
}

function demoteCropToCartonPreview(crop: MasterCropData): void {
  crop.classificationType = 'carton';
}

function clearDeletionMark(crop: MasterCropData): void {
  crop.markedForDeletion = false;
}

export function useMasterLabelEditor({ notify }: UseMasterLabelEditorOptions) {
  const [baseUpcData, setBaseUpcData] = useState<MasterUPCData[]>([]);
  const [upcData, setUpcData] = useState<MasterUPCData[]>([]);
  const [selectedCropIds, setSelectedCropIds] = useState<Set<string>>(new Set());
  const [activeDesignId, setActiveDesignId] = useState<string | null>(null);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [recentlyMovedCropIds, setRecentlyMovedCropIds] = useState<Set<string>>(new Set());

  const movedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastClickedCropRef = useRef<string | null>(null);

  const mode: ActionMode = selectedCropIds.size > 0 ? 'crops-selected' : 'idle';

  useEffect(() => {
    if (mode !== 'idle') setActiveDesignId(null);
  }, [mode]);

  useEffect(() => () => {
    if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
  }, []);

  const diffSummary: MasterLabelDiffSummary = useMemo(
    () => computeMasterLabelDiffSummary(baseUpcData, upcData),
    [baseUpcData, upcData]
  );

  const designColorMap = useMemo(() => {
    const map = new Map<string, string>();
    let idx = 0;
    for (const upc of upcData) {
      for (const design of upc.designs) {
        if (!map.has(design.id)) {
          map.set(design.id, DESIGN_COLORS[idx % DESIGN_COLORS.length]);
          idx += 1;
        }
      }
    }
    return map;
  }, [upcData]);

  const pushUndo = useCallback((label: string) => {
    setUndoStack((prev) => [...prev, { label, upcData: cloneUpcs(upcData) }]);
  }, [upcData]);

  const clearSelection = useCallback(() => {
    lastClickedCropRef.current = null;
    setSelectedCropIds(new Set());
  }, []);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setUpcData(last.upcData);
      clearSelection();
      setActiveDesignId(null);
      notify(`Undid: ${last.label}`, 'warning');
      return prev.slice(0, -1);
    });
  }, [clearSelection, notify]);

  const markMoved = useCallback((cropIds: string[]) => {
    if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
    setRecentlyMovedCropIds(new Set(cropIds));
    movedTimerRef.current = setTimeout(
      () => setRecentlyMovedCropIds(new Set()),
      MASTER_LABEL_MOVED_HIGHLIGHT_MS
    );
  }, []);

  const findCropLocation = useCallback((cropId: string, data: MasterUPCData[]): CropLocation | null => {
    for (let upcIdx = 0; upcIdx < data.length; upcIdx += 1) {
      const upc = data[upcIdx];
      for (let designIdx = 0; designIdx < upc.designs.length; designIdx += 1) {
        const cropIdx = upc.designs[designIdx].crops.findIndex((crop) => crop.id === cropId);
        if (cropIdx !== -1) return { upcIdx, section: 'design', designIdx, cropIdx };
      }
      const pendingIdx = upc.pendingCrops?.findIndex((crop) => crop.id === cropId) ?? -1;
      if (pendingIdx !== -1) return { upcIdx, section: 'pending', cropIdx: pendingIdx };
      const unassignedIdx = upc.unassignedCrops.findIndex((crop) => crop.id === cropId);
      if (unassignedIdx !== -1) return { upcIdx, section: 'no-design', cropIdx: unassignedIdx };
      const cartonIdx = upc.cartonCrops.findIndex((crop) => crop.id === cropId);
      if (cartonIdx !== -1) return { upcIdx, section: 'carton', cropIdx: cartonIdx };
      const inactiveIdx = upc.inactiveCrops?.findIndex((crop) => crop.id === cropId) ?? -1;
      if (inactiveIdx !== -1) return { upcIdx, section: 'inactive', cropIdx: inactiveIdx };
    }
    return null;
  }, []);

  const removeCropById = useCallback((cropId: string, data: MasterUPCData[]): MasterCropData | null => {
    for (const upc of data) {
      for (const design of upc.designs) {
        const idx = design.crops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return design.crops.splice(idx, 1)[0];
      }
      if (upc.pendingCrops) {
        const idx = upc.pendingCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.pendingCrops.splice(idx, 1)[0];
      }
      {
        const idx = upc.unassignedCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.unassignedCrops.splice(idx, 1)[0];
      }
      {
        const idx = upc.cartonCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.cartonCrops.splice(idx, 1)[0];
      }
      if (upc.inactiveCrops) {
        const idx = upc.inactiveCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.inactiveCrops.splice(idx, 1)[0];
      }
    }
    return null;
  }, []);

  const insertIntoTarget = useCallback((crop: MasterCropData, target: SectionTarget, next: MasterUPCData[]) => {
    const targetItem = next.find((item) => item.itemNumber === target.itemNumber);
    if (!targetItem) return;

    crop.isInactive = false;
    crop.isPending = false;
    crop.isMismatch = false;
    crop.reviewIssueType = null;
    clearDeletionMark(crop);

    if (target.type === 'design' && target.designId) {
      const design = targetItem.designs.find((item) => item.id === target.designId);
      if (design) {
        promoteCropToProductPreview(crop);
        applyCropBarcode(crop, resolveDesignTargetBarcode(design, targetItem));
        design.crops.push(crop);
      }
      return;
    }
    if (target.type === 'pending') {
      if (!targetItem.pendingCrops) targetItem.pendingCrops = [];
      crop.isInactive = true;
      crop.isPending = true;
      targetItem.pendingCrops.push(crop);
      return;
    }
    if (target.type === 'no-design') {
      promoteCropToProductPreview(crop);
      targetItem.unassignedCrops.push(crop);
      return;
    }
    if (target.type === 'carton') {
      crop.isInactive = false;
      demoteCropToCartonPreview(crop);
      targetItem.cartonCrops.push(crop);
      return;
    }
    if (target.type === 'inactive') {
      if (!targetItem.inactiveCrops) targetItem.inactiveCrops = [];
      crop.isInactive = true;
      targetItem.inactiveCrops.push(crop);
    }
  }, []);

  const moveSelectedCrops = useCallback((target: SectionTarget) => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    pushUndo(`Move ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      const cropsToMove: MasterCropData[] = [];
      for (const cropId of ids) {
        const crop = removeCropById(cropId, next);
        if (crop) cropsToMove.push(crop);
      }
      for (const crop of cropsToMove) insertIntoTarget(crop, target, next);
      for (const upc of next) {
        upc.designs.forEach((design) => {
          design.crops = sortDesignCrops(design.crops);
          normalizeRepresentativeCrop(design);
        });
        cleanupDesigns(upc);
      }
      return next;
    });

    clearSelection();
    markMoved(ids);
    notify(`Moved ${ids.length} crop${ids.length > 1 ? 's' : ''}`);
  }, [clearSelection, insertIntoTarget, markMoved, notify, pushUndo, removeCropById, selectedCropIds]);

  const deactivateCrops = useCallback(() => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    pushUndo(`Deactivate ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const moved: MasterCropData[] = [];
        for (const design of upc.designs) {
          const removed = design.crops.filter((crop) => idSet.has(crop.id));
          design.crops = design.crops.filter((crop) => !idSet.has(crop.id));
          moved.push(...removed);
          normalizeRepresentativeCrop(design);
        }
        if (upc.pendingCrops) {
          moved.push(...upc.pendingCrops.filter((crop) => idSet.has(crop.id)));
          upc.pendingCrops = upc.pendingCrops.filter((crop) => !idSet.has(crop.id));
        }
        moved.push(...upc.unassignedCrops.filter((crop) => idSet.has(crop.id)));
        upc.unassignedCrops = upc.unassignedCrops.filter((crop) => !idSet.has(crop.id));
        moved.push(...upc.cartonCrops.filter((crop) => idSet.has(crop.id)));
        upc.cartonCrops = upc.cartonCrops.filter((crop) => !idSet.has(crop.id));
        if (!upc.inactiveCrops) upc.inactiveCrops = [];
        for (const crop of moved) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
          crop.reviewIssueType = null;
          clearDeletionMark(crop);
        }
        upc.inactiveCrops.push(...moved);
        upc.inactiveCrops = sortCrops(upc.inactiveCrops);
        cleanupDesigns(upc);
      }
      return next;
    });

    clearSelection();
    markMoved(ids);
    notify(`Deactivated ${ids.length} crop${ids.length > 1 ? 's' : ''}`, 'warning');
  }, [clearSelection, markMoved, notify, pushUndo, selectedCropIds]);

  const createNewDesign = useCallback((itemNumber: string, _options?: { normalizedBarcode?: string | null }) => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    if (!upcData.some((item) => item.itemNumber === itemNumber)) return;
    const selectedCrops = upcData.flatMap((item) => [
      ...item.designs.flatMap((design) => design.crops),
      ...(item.pendingCrops ?? []),
      ...item.unassignedCrops,
      ...item.cartonCrops,
      ...(item.inactiveCrops ?? []),
    ]).filter((crop) => ids.includes(crop.id));
    const draftBarcode = _options?.normalizedBarcode || resolveDraftDesignVerifiedBarcode(selectedCrops);
    if (!draftBarcode) {
      notify('New designs require at least one scan or forge crop with a barcode', 'error');
      return;
    }
    pushUndo(`Create design from ${ids.length} crop(s)`);
    const draftDesignId = makeDraftDesignId();

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      const targetUpc = next.find((item) => item.itemNumber === itemNumber);
      if (!targetUpc) return prev;

      const crops: MasterCropData[] = [];
      for (const cropId of ids) {
        const crop = removeCropById(cropId, next);
        if (crop) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
          crop.reviewIssueType = null;
          clearDeletionMark(crop);
          crops.push(crop);
        }
      }

      for (const upcEntry of next) {
        upcEntry.designs.forEach((design) => {
          normalizeRepresentativeCrop(design);
        });
        cleanupDesigns(upcEntry);
      }

      if (crops.length > 0) {
        crops.forEach((crop) => {
          promoteCropToProductPreview(crop);
          applyCropBarcode(crop, draftBarcode);
        });
        targetUpc.designs.push({
          id: draftDesignId,
          persistedDesignId: null,
          representativeCropId: crops[0].id,
          barcodeStatus: 'unconfirmed',
          normalizedBarcode: draftBarcode,
          crops: sortDesignCrops(crops),
        });
        targetUpc.designs.sort((a, b) => a.id.localeCompare(b.id));
      }
      return next;
    });

    clearSelection();
    markMoved(ids);
    notify('Created draft design for barcode');
  }, [clearSelection, markMoved, notify, pushUndo, removeCropById, selectedCropIds, upcData]);

  const setRepresentative = useCallback((designId: string, cropId: string) => {
    let targetDesign: MasterUPCData['designs'][number] | null = null;
    let targetCrop: MasterCropData | null = null;
    for (const upc of upcData) {
      const design = upc.designs.find((item) => item.id === designId);
      if (!design) continue;
      targetDesign = design;
      targetCrop = design.crops.find((crop) => crop.id === cropId) ?? null;
      break;
    }
    if (!targetDesign || !targetCrop) return;

    const hasFrontAlternative = targetDesign.crops.some(
      (crop) =>
        crop.id !== cropId &&
        crop.classificationType !== 'carton' &&
        crop.orientation !== 'not-front'
    );
    if (
      targetCrop.classificationType !== 'carton' &&
      targetCrop.orientation === 'not-front' &&
      hasFrontAlternative
    ) {
      notify('Cannot use a non-front crop as representative while a front crop exists', 'warning');
      return;
    }

    pushUndo(`Set rep for ${designId.slice(0, 8)}`);
    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const design = upc.designs.find((item) => item.id === designId);
        if (design) {
          design.representativeCropId = cropId;
          normalizeRepresentativeCrop(design);
          break;
        }
      }
      return next;
    });
    clearSelection();
    notify(`Set representative for ${designId.slice(0, 8)}`);
  }, [clearSelection, notify, pushUndo, upcData]);

  const setSelectedReviewIssue = useCallback((reviewIssueType: MasterLabelReviewIssueType) => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    pushUndo(`Set review issue for ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const groups = [
          ...upc.designs.map((design) => design.crops),
          ...(upc.pendingCrops ? [upc.pendingCrops] : []),
          upc.unassignedCrops,
          upc.cartonCrops,
          ...(upc.inactiveCrops ? [upc.inactiveCrops] : []),
        ];
        for (const group of groups) {
          for (const crop of group) {
            if (!idSet.has(crop.id)) continue;
            crop.reviewIssueType = reviewIssueType;
            clearDeletionMark(crop);
          }
        }
      }
      return next;
    });

    clearSelection();
    markMoved(ids);
    notify(`Updated review issue for ${ids.length} crop${ids.length > 1 ? 's' : ''}`);
  }, [clearSelection, markMoved, notify, pushUndo, selectedCropIds]);

  const clearSelectedReviewIssue = useCallback(() => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    pushUndo(`Clear review issue for ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const groups = [
          ...upc.designs.map((design) => design.crops),
          ...(upc.pendingCrops ? [upc.pendingCrops] : []),
          upc.unassignedCrops,
          upc.cartonCrops,
          ...(upc.inactiveCrops ? [upc.inactiveCrops] : []),
        ];
        for (const group of groups) {
          for (const crop of group) {
            if (!idSet.has(crop.id)) continue;
            crop.reviewIssueType = null;
          }
        }
      }
      return next;
    });

    clearSelection();
    markMoved(ids);
    notify(`Cleared review issue for ${ids.length} crop${ids.length > 1 ? 's' : ''}`);
  }, [clearSelection, markMoved, notify, pushUndo, selectedCropIds]);

  const markSelectedForDeletion = useCallback(() => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    pushUndo(`Mark ${ids.length} crop(s) for deletion`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const groups = [
          ...upc.designs.map((design) => design.crops),
          ...(upc.pendingCrops ? [upc.pendingCrops] : []),
          upc.unassignedCrops,
          upc.cartonCrops,
          ...(upc.inactiveCrops ? [upc.inactiveCrops] : []),
        ];
        for (const group of groups) {
          for (const crop of group) {
            if (!idSet.has(crop.id)) continue;
            crop.markedForDeletion = true;
          }
        }
      }
      return next;
    });

    clearSelection();
    markMoved(ids);
    notify(`Marked ${ids.length} crop${ids.length > 1 ? 's' : ''} for deletion`, 'warning');
  }, [clearSelection, markMoved, notify, pushUndo, selectedCropIds]);

  const handleSectionClick = useCallback((target: SectionTarget) => {
    if (mode === 'crops-selected') {
      if (target.type === 'pending' || target.type === 'barcode') return;
      const allAlreadyInTarget = [...selectedCropIds].every((cropId) => {
        const location = findCropLocation(cropId, upcData);
        if (!location) return false;
        const sourceItemNumber = upcData[location.upcIdx].itemNumber;
        if (sourceItemNumber !== target.itemNumber) return false;
        if (target.type === 'design' && target.designId && location.section === 'design') {
          return upcData[location.upcIdx].designs[location.designIdx].id === target.designId;
        }
        return (
          (target.type === 'no-design' && location.section === 'no-design') ||
          (target.type === 'carton' && location.section === 'carton') ||
          (target.type === 'inactive' && location.section === 'inactive')
        );
      });
      if (allAlreadyInTarget) {
        clearSelection();
        return;
      }
      moveSelectedCrops(target);
      return;
    }

    if (mode === 'idle') {
      if (target.type === 'barcode') return;
      const item = upcData.find((entry) => entry.itemNumber === target.itemNumber);
      if (!item) return;
      let cropIds: string[] = [];
      if (target.type === 'design' && target.designId) {
        cropIds = item.designs.find((design) => design.id === target.designId)?.crops.map((crop) => crop.id) ?? [];
      } else if (target.type === 'pending') {
        cropIds = (item.pendingCrops ?? []).map((crop) => crop.id);
      } else if (target.type === 'no-design') {
        cropIds = item.unassignedCrops.map((crop) => crop.id);
      } else if (target.type === 'carton') {
        cropIds = item.cartonCrops.map((crop) => crop.id);
      } else if (target.type === 'inactive') {
        cropIds = (item.inactiveCrops ?? []).map((crop) => crop.id);
      }
      if (cropIds.length > 0) setSelectedCropIds(new Set(cropIds));
    }
  }, [clearSelection, findCropLocation, mode, moveSelectedCrops, selectedCropIds, upcData]);

  const getGroupCropIds = useCallback((cropId: string): string[] => {
    for (const upc of upcData) {
      for (const design of upc.designs) {
        if (design.crops.some((crop) => crop.id === cropId)) {
          return sortDesignCrops(design.crops).map((crop) => crop.id);
        }
      }
      if (upc.pendingCrops?.some((crop) => crop.id === cropId)) return sortCrops(upc.pendingCrops).map((crop) => crop.id);
      if (upc.unassignedCrops.some((crop) => crop.id === cropId)) return sortCrops(upc.unassignedCrops).map((crop) => crop.id);
      if (upc.cartonCrops.some((crop) => crop.id === cropId)) return sortCrops(upc.cartonCrops).map((crop) => crop.id);
      if (upc.inactiveCrops?.some((crop) => crop.id === cropId)) return sortCrops(upc.inactiveCrops).map((crop) => crop.id);
    }
    return [];
  }, [upcData]);

  const setSelectedCropOrientation = useCallback((orientation: 'front' | 'not-front') => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const selectedCrops = upcData.flatMap((item) => [
      ...item.designs.flatMap((design) => design.crops),
      ...(item.pendingCrops ?? []),
      ...item.unassignedCrops,
      ...item.cartonCrops,
      ...(item.inactiveCrops ?? []),
    ]).filter((crop) => selectedCropIds.has(crop.id));
    const ignoredCarton = selectedCrops.some((crop) => crop.classificationType === 'carton');
    const changedCropIds = selectedCrops
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation !== orientation)
      .map((crop) => crop.id);

    if (changedCropIds.length === 0) {
      if (ignoredCarton) notify('Orientation shortcuts ignore carton crops', 'warning');
      return;
    }

    pushUndo(`Set ${changedCropIds.length} crop(s) to ${orientation}`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      const changedCropIdSet = new Set(changedCropIds);
      for (const upc of next) {
        for (const design of upc.designs) {
          for (const crop of design.crops) {
            if (!changedCropIdSet.has(crop.id)) continue;
            crop.orientation = orientation;
          }
          design.crops = sortDesignCrops(design.crops);
          normalizeRepresentativeCrop(design);
        }
        const nonDesignGroups = [
          ...(upc.pendingCrops ? [upc.pendingCrops] : []),
          upc.unassignedCrops,
          upc.cartonCrops,
          ...(upc.inactiveCrops ? [upc.inactiveCrops] : []),
        ];
        for (const group of nonDesignGroups) {
          for (const crop of group) {
            if (!changedCropIdSet.has(crop.id)) continue;
            crop.orientation = orientation;
          }
          group.splice(0, group.length, ...sortCrops(group));
        }
      }
      return next;
    });

    clearSelection();
    markMoved(changedCropIds);
    notify(`Updated ${changedCropIds.length} crop${changedCropIds.length > 1 ? 's' : ''} to ${orientation}`);
  }, [clearSelection, markMoved, notify, pushUndo, selectedCropIds, upcData]);

  const toggleCropSelection = useCallback((cropId: string, shiftKey = false) => {
    if (shiftKey && lastClickedCropRef.current && lastClickedCropRef.current !== cropId) {
      const groupCropIds = getGroupCropIds(cropId);
      const lastIdx = groupCropIds.indexOf(lastClickedCropRef.current);
      const currentIdx = groupCropIds.indexOf(cropId);
      if (lastIdx !== -1 && currentIdx !== -1) {
        const start = Math.min(lastIdx, currentIdx);
        const end = Math.max(lastIdx, currentIdx);
        const range = groupCropIds.slice(start, end + 1);
        setSelectedCropIds((prev) => {
          const next = new Set(prev);
          for (const id of range) next.add(id);
          return next;
        });
        lastClickedCropRef.current = cropId;
        return;
      }
    }
    lastClickedCropRef.current = cropId;
    setSelectedCropIds((prev) => {
      const next = new Set(prev);
      if (next.has(cropId)) next.delete(cropId);
      else next.add(cropId);
      return next;
    });
  }, [getGroupCropIds]);

  const selectedCropDesignInfo: SelectedCropDesignInfo | null = useMemo(() => {
    if (selectedCropIds.size !== 1) return null;
    const cropId = [...selectedCropIds][0];
    for (const upc of upcData) {
      for (const design of upc.designs) {
        if (design.crops.some((crop) => crop.id === cropId)) {
          return {
            designId: design.id,
            cropId,
            isAlreadyRep: design.representativeCropId === cropId,
          };
        }
      }
    }
    return null;
  }, [selectedCropIds, upcData]);

  const selectedCropItemNumber = useMemo(() => {
    if (selectedCropIds.size === 0) return null;
    const upcs = new Set<string>();
    for (const cropId of selectedCropIds) {
      const location = findCropLocation(cropId, upcData);
      if (location) upcs.add(upcData[location.upcIdx].itemNumber);
    }
    return upcs.size === 1 ? [...upcs][0] : null;
  }, [findCropLocation, selectedCropIds, upcData]);

  const replaceAllUpcs = useCallback((nextUpcs: MasterUPCData[]) => {
    const cloned = cloneUpcs(nextUpcs);
    setBaseUpcData(cloned);
    setUpcData(cloneUpcs(cloned));
    clearSelection();
    setActiveDesignId(null);
    setUndoStack([]);
  }, [clearSelection]);

  const appendLoadedUpc = useCallback((nextUpc: MasterUPCData) => {
    const cloned = structuredClone(nextUpc);
    setBaseUpcData((prev) => [...prev.filter((item) => item.itemNumber !== cloned.itemNumber), structuredClone(cloned)]);
    setUpcData((prev) => [...prev.filter((item) => item.itemNumber !== cloned.itemNumber), cloned]);
  }, []);

  const patchUpcMetadata = useCallback((itemNumber: string, updater: (current: MasterUPCData) => MasterUPCData) => {
    setBaseUpcData((prev) => prev.map((item) => (item.itemNumber === itemNumber ? updater(item) : item)));
    setUpcData((prev) => prev.map((item) => (item.itemNumber === itemNumber ? updater(item) : item)));
  }, []);

  const removeUpc = useCallback((itemNumber: string) => {
    setBaseUpcData((prev) => prev.filter((item) => item.itemNumber !== itemNumber));
    setUpcData((prev) => prev.filter((item) => item.itemNumber !== itemNumber));
    clearSelection();
    setActiveDesignId(null);
  }, [clearSelection]);

  const rebaseUpcs = useCallback((freshUpcs: MasterUPCData[]) => {
    const freshByUpc = new Map(freshUpcs.map((upc) => [upc.itemNumber, structuredClone(upc)]));
    setBaseUpcData((prev) => prev.map((item) => freshByUpc.get(item.itemNumber) ?? item));
    setUpcData((prev) => prev.map((item) => structuredClone(freshByUpc.get(item.itemNumber) ?? item)));
    clearSelection();
    setActiveDesignId(null);
    setUndoStack([]);
  }, [clearSelection]);

  const upcHasChanges = useCallback((itemNumber: string) => diffSummary.dirtyItemNumbers.includes(itemNumber), [diffSummary.dirtyItemNumbers]);

  return {
    baseUpcData,
    upcData,
    selectedCropIds,
    setSelectedCropIds,
    toggleCropSelection,
    activeDesignId,
    setActiveDesignId,
    mode,
    designColorMap,
    diffSummary,
    upcHasChanges,
    undoStack,
    undo,
    deactivateCrops,
    markSelectedForDeletion,
    setSelectedReviewIssue,
    clearSelectedReviewIssue,
    createNewDesign,
    setRepresentative,
    handleSectionClick,
    selectedCropDesignInfo,
    selectedCropItemNumber,
    setSelectedCropOrientation,
    replaceAllUpcs,
    appendLoadedUpc,
    patchUpcMetadata,
    removeUpc,
    rebaseUpcs,
    recentlyMovedCropIds,
  };
}
